import { useMemo, useState } from "react";
import { ChevronDown, IndianRupee } from "lucide-react";
import { inr } from "../lib/api";
import { fmtDay } from "../lib/reminders";

const Row = ({ label, value, testid, strong }) => (
  <p className="flex justify-between gap-4 text-sm" data-testid={testid}>
    <span className="text-white/50">{label}</span>
    <span className={`text-right ${strong ? "font-semibold text-white" : ""}`}>{value}</span>
  </p>
);

export const FeeSummary = ({ s }) => {
  if (!s) return null;
  return (
    <>
      <h2 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4 flex items-center gap-2">
        <IndianRupee size={13} className="text-[#D4AF37]" /> My Fee Summary
      </h2>
      <div className="border border-[#D4AF37]/30 bg-[#0A0A0A] p-6 mb-10" data-testid="portal-fees">
        <div className="space-y-2">
          <Row label="Current Package" value={s.package || "—"} strong testid="fee-summary-package" />
          {s.period_start && <Row label="Package Period" value={`${fmtDay(s.period_start)} – ${fmtDay(s.period_end)}`} testid="fee-summary-period" />}
          {s.package_fee > 0 && <Row label="Package Fee" value={inr(s.package_fee)} testid="fee-summary-fee" />}
        </div>
        {s.lines.length > 0 && (
          <div className="border-t border-white/10 mt-5 pt-5" data-testid="fee-summary-breakdown">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Outstanding Breakdown</p>
            <ul className="space-y-1.5 text-sm text-white/75">
              {s.lines.map((l) => <li key={l}>{l}</li>)}
            </ul>
          </div>
        )}
        <div className="border-t border-white/10 mt-5 pt-5 space-y-2">
          <Row label="Amount Paid (this period)" value={inr(s.amount_paid || 0)} testid="fee-summary-paid" />
          {s.due_date && <Row label="Payment Due Date" value={fmtDay(s.due_date)} testid="fee-summary-due" />}
        </div>
        <div className={`mt-5 p-5 flex justify-between items-center border ${s.outstanding > 0 ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10"}`}>
          <span className="text-xs uppercase tracking-widest text-white/60">Outstanding Balance</span>
          <span className={`font-heading text-3xl font-black ${s.outstanding > 0 ? "text-[#D4AF37]" : "text-white"}`} data-testid="portal-outstanding">{inr(s.outstanding)}</span>
        </div>
      </div>
    </>
  );
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const mLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

// Collapsible section that first asks for a month, then lists that month's rows.
export const MonthDropdown = ({ title, rows, render, empty, testid }) => {
  const [open, setOpen] = useState(false);
  const months = useMemo(() => [...new Set(rows.map((r) => r.date.slice(0, 7)))].sort().reverse(), [rows]);
  const [month, setMonth] = useState("");
  const shown = rows.filter((r) => r.date.startsWith(month));
  return (
    <div className="border border-white/10 bg-[#0A0A0A] mb-4" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`${testid}-toggle`}>
        <span className="text-xs tracking-[0.25em] uppercase text-white/60 font-semibold">{title}</span>
        <ChevronDown size={16} className={`text-[#D4AF37] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5">
          {months.length === 0 ? <p className="text-white/40 text-sm">{empty}</p> : (
            <>
              <select value={month} onChange={(e) => setMonth(e.target.value)} className="w-full sm:w-64 bg-[#050505] border border-white/15 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37] mb-4" data-testid={`${testid}-month`}>
                <option value="">Choose month & year…</option>
                {months.map((m) => <option key={m} value={m}>{mLabel(m)}</option>)}
              </select>
              {month && <div className="border border-white/10" data-testid={`${testid}-list`}>{shown.map(render)}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
};
